import { useEffect, useState } from "react";
import { serverFetch, apiUrl } from "@server";
import { Checkbox, DatePicker, Input, message, Space, Table, Tag, Button } from "antd";
import { fmtDateTimeFull } from "@extensions/utils";
import { backupStatuses } from "@extensions/enums";
import { useSelector } from "react-redux";
import { DownloadOutlined } from "@ant-design/icons";
import { strDate2Server } from "@extensions/convert";
import { refreshButton } from "@extensions/design";
import dayjs from "dayjs";

const CompanyBackups = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [allCompanies, setAllCompanies] = useState(false);
  const [onlyActive, setOnlyActive] = useState(true);
  const [searchStr, setSearchStr] = useState('');
  const user = useSelector((s) => s.user);
  
  // Загрузка дат из localStorage
  const loadDatesFromStorage = () => {
    try {
      const stored = localStorage.getItem('companyBackups');
      if (stored) {
        const data = JSON.parse(stored);
        if (data.dateFrom && data.dateTo) {
          return {
            dateFrom: dayjs(data.dateFrom),
            dateTo: dayjs(data.dateTo)
          };
        }
      }
    } catch (e) {
      console.error('Error loading dates from localStorage:', e);
    }
    // Если нет сохраненных дат - используем текущие
    const today = dayjs();
    return {
      dateFrom: today.startOf('day'),
      dateTo: today.endOf('day')
    };
  };
  
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);

  // Инициализация дат из localStorage
  useEffect(() => {
    const dates = loadDatesFromStorage();
    setDateFrom(dates.dateFrom);
    setDateTo(dates.dateTo);
  }, []);

  // Сохранение дат в localStorage
  const saveDatesToStorage = (from, to) => {
    try {
      const data = {
        dateFrom: from ? from.toISOString() : null,
        dateTo: to ? to.toISOString() : null
      };
      localStorage.setItem('companyBackups', JSON.stringify(data));
    } catch (e) {
      console.error('Error saving dates to localStorage:', e);
    }
  };

  const handleDateFromChange = (value) => {
    setDateFrom(value);
    if (value && dateTo) {
      saveDatesToStorage(value, dateTo);
    } else if (!value) {
      // Если сбрасываем дату, очищаем localStorage
      saveDatesToStorage(null, null);
    }
  };

  const handleDateToChange = (value) => {
    setDateTo(value);
    if (dateFrom && value) {
      saveDatesToStorage(dateFrom, value);
    } else if (!value) {
      // Если сбрасываем дату, очищаем localStorage
      saveDatesToStorage(null, null);
    }
  };

  const getData = () => {
    if (!dateFrom || !dateTo) return;
    
    setLoading(true);
    const dateFromStr = strDate2Server(dateFrom);
    const dateToStr = strDate2Server(dateTo);
    serverFetch(`company/Backup?allCompanies=${allCompanies}&onlyActive=${onlyActive}&dateFrom=${dateFromStr}&dateTo=${dateToStr}`)
      .then(data => {
        setLoading(false);
        setItems(data);
      })
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage || "Ошибка загрузки");
      });
  };

  useEffect(() => {
    getData();
  }, [allCompanies, onlyActive, dateFrom, dateTo]);

  const formatBytes = (bytes) => {
    if (!bytes) return "0 МБ";
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} МБ`;
  };

  const handleDownload = async (id) => {
    const sessionId = localStorage.getItem('sessionId');
    const url = `${apiUrl}/api/company/Backup/Download/${id}`;
    
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: sessionId ? { 'SessionId': sessionId } : {}
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { userMessage: 'Ошибка скачивания файла' };
        }
        throw errorData;
      }
      
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = '';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      message.error(e.userMessage || 'Ошибка при скачивании файла');
    }
  };

  const columns = [
    { dataIndex: "id", title: "#", width: 80 },
    { 
      dataIndex: "createdOn", 
      title: "Дата время", 
      width: 180, 
      render: (value) => value ? fmtDateTimeFull(value) : "-"
    },
    { 
      dataIndex: "size", 
      title: "Размер", 
      width: 120,
      render: formatBytes
    },
    { 
      dataIndex: "sizeTake", 
      title: "Передано", 
      width: 120,
      render: formatBytes
    },
    { dataIndex: "backupGuid", title: "Код", width: 200 },
    { dataIndex: "companyName", title: "Компания", width: 200 },
    {
      dataIndex: "status",
      title: "Статус",
      width: 180,
      render: (status) => {
        const statusItem = backupStatuses.find(s => s.value === status);
        if (!statusItem) return "-";
        return <Tag color={statusItem.color}>{statusItem.label}</Tag>;
      }
    },
    {
      title: "",
      width: 100,
      render: (_, record) => {
        if ([2, 3, 4].includes(record.status)) {
          return (
            <Button
              type="link"
              icon={<DownloadOutlined />}
              onClick={() => handleDownload(record.id)}
              style={{ opacity: 0 }}
              className="backup-download-btn"
            >
              Скачать
            </Button>
          );
        }
        return null;
      }
    }
  ];

  const filtered = items.filter(i => {
    if (!searchStr) return true;
    const search = searchStr.toLowerCase();
    const companyName = (i.companyName || '').toLowerCase();
    const backupGuid = (i.backupGuid || '').toLowerCase();
    const id = String(i.id || '').toLowerCase();
    return companyName.includes(search) || backupGuid.includes(search) || id.includes(search);
  });

  return <div>
    <Space style={{ marginBottom: 16 }}>
      <Input 
        placeholder="Поиск (Компания, Код, #)" 
        value={searchStr} 
        onChange={e => setSearchStr(e.target.value)}
        style={{ width: 250 }}
      />
      <DatePicker 
        value={dateFrom} 
        onChange={handleDateFromChange} 
        format="DD.MM.YYYY" 
        allowClear={false} 
        style={{ width: 150 }}
        placeholder="С"
      />
      <DatePicker 
        value={dateTo} 
        onChange={handleDateToChange} 
        format="DD.MM.YYYY" 
        allowClear={false} 
        style={{ width: 150 }}
        placeholder="По"
      />
      {user?.admin && (
        <Checkbox 
          checked={allCompanies} 
          onChange={e => setAllCompanies(e.target.checked)}
        >
          По всем компаниям
        </Checkbox>
      )}
      <Checkbox 
        checked={onlyActive} 
        onChange={e => setOnlyActive(e.target.checked)}
      >
        Только активные
      </Checkbox>
      {refreshButton(getData)}
    </Space>
    
    <Table
      dataSource={filtered}
      loading={loading}
      columns={columns}
      rowKey="id"
      pagination={false}
      size="small"
      scroll={{ x: 'max-content' }}
      onRow={(record) => ({
        onMouseEnter: (e) => {
          const btn = e.currentTarget.querySelector('.backup-download-btn');
          if (btn) btn.style.opacity = '1';
        },
        onMouseLeave: (e) => {
          const btn = e.currentTarget.querySelector('.backup-download-btn');
          if (btn) btn.style.opacity = '0';
        }
      })}
    />
  </div>;
};

export default CompanyBackups;

